import {
  SEARCH_TEXT_PARAMETER,
  PAGE_PARAMETER,
  LABELS_PARAMETER,
  FOLDER_PARAMETER,
  ENTITY_TYPE_PARAMETER,
  ISSUE_TYPE_PARAMETER,
  VERTEX_ID,
  VERTEX_TYPE,
  VERTEX_CONTEXT,
  USER_TAGS_PARAMETER,
  CAPTION_PARAMETER,
  ISSUE_CONFIDENCE,
  DUPLICATE_THRESHOLD,
  SEARCH_TEXT_THRESHOLD_PARAMETER,
  UNIQUENESS_SCORE_PARAMETER,
  METADATA_SOURCES_PARAMETER,
  GRANULARITY_PARAMETER,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";
import { isVqlQuery } from "./vqlHelpers";
import { SingleDatasetState } from "redux/SingleDataset/types";
import {
  isValidEntityType,
  APIQueryParams,
  FiltersObject,
  MetadataItem,
  EntityType,
} from "types";

export const buildAPIQueryParams = (
  state: SingleDatasetState,
  queryParams: FiltersObject = {}
): APIQueryParams => {
  const duplicateThreshold = queryParams[DUPLICATE_THRESHOLD];
  const newDuplicatesThreshold =
    !!queryParams[VQL_PARAMETER] &&
    !!JSON.parse(queryParams[VQL_PARAMETER]).find((query: any) =>
      isVqlQuery(query, "duplicates")
    );

  const queryObject: APIQueryParams = {};
  let similarityThreshold = "2";
  if (queryParams[GRANULARITY_PARAMETER]) {
    similarityThreshold = queryParams[GRANULARITY_PARAMETER];
  } else if (
    Array.isArray(state.similarityThresholds) &&
    state.similarityThresholds?.length > 0
  ) {
    if (state.similarityThresholds?.length <= 2) {
      similarityThreshold = state.similarityThresholds[0].toString();
    } else {
      similarityThreshold =
        state.similarityThresholds[
          Math.floor(state.similarityThresholds?.length / 2)
        ].toString();
    }
  }

  const isDuplicateApplied = !!duplicateThreshold || !!newDuplicatesThreshold;

  queryObject.threshold = isDuplicateApplied ? "0" : similarityThreshold;
  if (duplicateThreshold) {
    queryObject.duplicate_threshold = `${duplicateThreshold}`;
  }

  if (queryParams[LABELS_PARAMETER]) {
    queryObject.labels = queryParams[LABELS_PARAMETER];
  }

  if (queryParams[ISSUE_TYPE_PARAMETER]) {
    queryObject.issues = queryParams[ISSUE_TYPE_PARAMETER];
  }

  if (state.fileNameSearchText) {
    queryObject.image_filename = state.fileNameSearchText;
  }
  if (queryParams[SEARCH_TEXT_PARAMETER]) {
    queryObject.caption = queryParams[SEARCH_TEXT_PARAMETER];
  }
  if (queryParams[SEARCH_TEXT_THRESHOLD_PARAMETER]) {
    queryObject.textual_similarity_threshold = `${queryParams[SEARCH_TEXT_THRESHOLD_PARAMETER]}`;
  }
  if (queryParams[FOLDER_PARAMETER]) {
    queryObject.path = queryParams[FOLDER_PARAMETER];
  }
  if (queryParams[VERTEX_TYPE] === "UPLOAD") {
    queryObject.anchor_type = "UPLOAD";
  }
  if (queryParams[VERTEX_CONTEXT]) {
    queryObject.anchor_context = queryParams[VERTEX_CONTEXT];
  }

  const entityTypeValue = getEntityTypeValue(
    state.entityTypeFilter,
    isValidEntityType(queryParams[ENTITY_TYPE_PARAMETER])
      ? queryParams[ENTITY_TYPE_PARAMETER]
      : null
  );
  if (entityTypeValue) {
    queryObject.entity_type = entityTypeValue.toUpperCase();
  }

  if (queryParams[PAGE_PARAMETER]) {
    queryObject.page_number = `${parseInt(queryParams[PAGE_PARAMETER]) - 1}`;
  }

  if (queryParams[USER_TAGS_PARAMETER]) {
    queryObject.tags = queryParams[USER_TAGS_PARAMETER];
  }
  if (queryParams[VERTEX_ID]) {
    queryObject.anchor_media_id = queryParams[VERTEX_ID];
  }

  if (queryParams[ISSUE_CONFIDENCE]) {
    const issueConfidence = JSON.parse(queryParams[ISSUE_CONFIDENCE]);
    if (issueConfidence.length === 2) {
      queryObject.issue_confidence = `(${issueConfidence[0]},${issueConfidence[1]})`;
    }
  }

  if (queryParams[CAPTION_PARAMETER]) {
    queryObject.caption_only_filter = queryParams[CAPTION_PARAMETER];
  }

  if (queryParams[METADATA_SOURCES_PARAMETER]) {
    queryObject.metadata_sources = queryParams[METADATA_SOURCES_PARAMETER];
  }

  if (queryParams[UNIQUENESS_SCORE_PARAMETER]) {
    queryObject.uniqueness_score = `${queryParams[UNIQUENESS_SCORE_PARAMETER]}`;
  }

  if (queryParams[VQL_PARAMETER]) {
    queryObject.vql = queryParams[VQL_PARAMETER];
  }

  return queryObject;
};

export const getEntityTypeValue = (
  entityTypeFilter: MetadataItem[],
  entityTypeParamValue: string | null
): EntityType => {
  let result: EntityType = "IMAGES";
  const entityTypeValues = [...entityTypeFilter];
  if (entityTypeValues) {
    if (entityTypeParamValue === null) {
      result = entityTypeValues[0].text.toUpperCase() as EntityType;
    } else {
      result = entityTypeValues
        .find((value: MetadataItem) =>
          entityTypeParamValue.toUpperCase().includes(value.text.toUpperCase())
        )
        ?.text.toUpperCase() as EntityType | "IMAGES";
    }
  }
  return result;
};
